import { useEffect } from 'react'

import { Outlet } from '@tanstack/react-router'

import { useGetPublicWhiteLabel } from '@/api/v1/public/white-label'
import { BootSpinner } from '@/components/common/boot-spinner'
import { PageTitleManager } from '@/components/common/page-title-manager'
import { UpdateDialog } from '@/components/common/update-dialog'
import { Toaster } from '@/components/sui/sonner'
import { usePageTracking } from '@/hooks/use-page-tracking'
import { useTrackingCookies } from '@/hooks/use-tracking-cookies'
import { useVersionCheck } from '@/hooks/use-version-check'
import { logger } from '@/lib/logger'
import {
  applyWhiteLabelTheme,
  updateFavicon,
  updateManifest,
} from '@/lib/white-label'
import { useAuthStore } from '@/stores/auth'
import { useWhiteLabelStore } from '@/stores/white-label'

import { GleapBootstrap } from './components/gleap-bootstrap'

export default function RootLayout() {
  const {
    setWhiteLabel,
    setLoading,
    isLoading: isStoreLoading,
  } = useWhiteLabelStore()
  const isAuthenticated = useAuthStore((state) => !!state.accessToken)

  // Initialize tracking cookies
  useTrackingCookies()

  // Track page views on route changes
  usePageTracking()

  // Show dialog when a new version is deployed
  const { isNewVersionAvailable, dismiss } = useVersionCheck()

  // Fetch white-label settings only when not authenticated
  const {
    data: whiteLabelData,
    isLoading: isQueryLoading,
    error,
  } = useGetPublicWhiteLabel()

  useEffect(() => {
    if (!isAuthenticated) {
      if (whiteLabelData) {
        // Apply white-label customizations (title is managed by usePageTitle hook)
        if (whiteLabelData.isWhiteLabel) {
          applyWhiteLabelTheme(whiteLabelData.theme)
          updateFavicon(whiteLabelData.faviconUrl)
          updateManifest(whiteLabelData.companyName, whiteLabelData.faviconUrl)
        }

        setWhiteLabel(whiteLabelData)
      }

      if (error) {
        logger.error(
          'Failed to fetch white-label settings',
          error instanceof Error ? error : undefined,
        )
        setLoading(false)
      }

      // Stop loading when query is done
      if (!isQueryLoading) {
        setLoading(false)
      }
    } else {
      // If authenticated, no need to load white-label
      setLoading(false)
    }
  }, [
    isAuthenticated,
    whiteLabelData,
    isQueryLoading,
    error,
    setWhiteLabel,
    setLoading,
  ])

  // Show loading screen while fetching white-label (only for unauthenticated users)
  if (!isAuthenticated && (isStoreLoading || isQueryLoading)) {
    return <BootSpinner />
  }

  return (
    <>
      <PageTitleManager />
      <GleapBootstrap />
      <Outlet />
      <Toaster position="top-center" />
      <UpdateDialog
        open={isNewVersionAvailable}
        onOpenChange={(open) => {
          if (!open) dismiss()
        }}
      />
    </>
  )
}
